import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchActivity } from '../api';
import { Empty, ErrorBox, Loading, RelativeTime } from '../components/ui';

const KIND: Record<string, { icon: string; label: string; tone: string }> = {
  commit: { icon: '◆', label: 'commit', tone: 'text-[var(--color-ink-muted)]' },
  pr_open: { icon: '◇', label: 'PR abierto', tone: 'text-amber-700 dark:text-amber-400' },
  pr_merged: { icon: '◈', label: 'PR fusionado', tone: 'text-emerald-700 dark:text-emerald-400' },
  pr_closed: { icon: '◌', label: 'PR cerrado', tone: 'text-[var(--color-ink-muted)]' },
  ci_run: { icon: '▸', label: 'CI', tone: 'text-[var(--color-ink-muted)]' },
  ci_failed: { icon: '■', label: 'CI fallida', tone: 'text-red-700 dark:text-red-400' },
  release: { icon: '★', label: 'release', tone: 'text-emerald-700 dark:text-emerald-400' },
  deployment: { icon: '⬆', label: 'despliegue', tone: 'text-emerald-700 dark:text-emerald-400' },
};

const FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todo' },
  { value: 'commit', label: 'Commits' },
  { value: 'pr_open', label: 'PRs abiertos' },
  { value: 'pr_merged', label: 'PRs fusionados' },
  { value: 'ci_failed', label: 'CI fallida' },
];

export function ActivityPage(): JSX.Element {
  const [kind, setKind] = useState('');
  const query = useQuery({ queryKey: ['activity'], queryFn: () => fetchActivity(200) });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const entries = kind === '' ? query.data : query.data.filter((entry) => entry.kind === kind);

  return (
    <>
      <h1 className="mb-1 text-lg font-semibold">Actividad reciente</h1>
      <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
        Commits, pull requests, ejecuciones de CI, releases y despliegues de todos los proyectos.
      </p>

      <div className="mb-4 flex flex-wrap gap-1">
        {FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setKind(filter.value)}
            className={`rounded px-2.5 py-1 text-[12px] ${
              kind === filter.value
                ? 'bg-[var(--color-surface-muted)] font-medium'
                : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {entries.length === 0 ? (
        <Empty>Sin actividad para este filtro.</Empty>
      ) : (
        <ol className="rounded-lg border border-[var(--color-border-subtle)]">
          {entries.map((entry, index) => {
            const meta = KIND[entry.kind] ?? {
              icon: '·',
              label: entry.kind,
              tone: 'text-[var(--color-ink-muted)]',
            };
            return (
              <li
                key={`${entry.kind}-${entry.at}-${index}`}
                className="flex items-baseline gap-2.5 border-b border-[var(--color-border-subtle)] px-3 py-1.5 text-[13px] last:border-0"
              >
                <span className={`w-3 shrink-0 text-[10px] ${meta.tone}`} title={meta.label}>
                  {meta.icon}
                </span>
                <span className="w-16 shrink-0 text-[11px] text-[var(--color-ink-muted)]">
                  <RelativeTime iso={entry.at} />
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {entry.url ? (
                    <a
                      href={entry.url}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:underline"
                    >
                      {entry.title}
                    </a>
                  ) : (
                    entry.title
                  )}
                </span>
                <span className="hidden shrink-0 text-[11px] text-[var(--color-ink-muted)] sm:inline">
                  {entry.project_slug}
                </span>
                <span className="hidden w-24 shrink-0 truncate text-right text-[11px] text-[var(--color-ink-muted)] md:inline">
                  {entry.actor ?? ''}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </>
  );
}
